import React, { useState, useEffect } from "react";
import {
  Key,
  Plus,
  RefreshCw,
  Copy,
  Check,
  Eye,
  EyeOff,
  Trash2,
  Users,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Sliders,
  ShieldCheck,
  ArrowDownRight,
  ExternalLink,
  Layers,
  Sparkles,
  Smartphone,
  Filter
} from "lucide-react";
import { TgApiCredential } from "../types";

interface ApiCredentialPoolSectionProps {
  appTheme?: "dark" | "light" | "apple";
  currentUser?: {
    username?: string;
    role?: string;
    permissions?: any;
  } | null;
  accounts?: Array<{
    phoneNumber: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    apiId?: number;
    owner?: string;
    status?: string;
  }>;
  onSelectApiForLogin?: (apiId: number, apiHash: string) => void;
  refreshTrigger?: number;
}

export const ApiCredentialPoolSection: React.FC<ApiCredentialPoolSectionProps> = ({
  appTheme = "dark",
  currentUser,
  accounts = [],
  onSelectApiForLogin,
  refreshTrigger = 0,
}) => {
  const [credentials, setCredentials] = useState<TgApiCredential[]>([]);
  const [summary, setSummary] = useState<{
    totalCredentials: number;
    activeCredentialsCount: number;
    fullCredentialsCount: number;
    totalMountedAccounts: number;
    fallbackApiId: number;
    fallbackApiHash: string;
  }>({
    totalCredentials: 0,
    activeCredentialsCount: 0,
    fullCredentialsCount: 0,
    totalMountedAccounts: 0,
    fallbackApiId: 94575,
    fallbackApiHash: "a3406de8d171bb422bb6ddf3bbd800e2",
  });
  const [recommendedCandidate, setRecommendedCandidate] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const effectiveUsername = (currentUser?.username || "admin").toLowerCase().trim();
  const isAdmin = currentUser?.role === "admin";
  const [selectedOwnerFilter, setSelectedOwnerFilter] = useState<string>("self");

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [newApiId, setNewApiId] = useState("");
  const [newApiHash, setNewApiHash] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newMaxAccounts, setNewMaxAccounts] = useState(5);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // UI helpers
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [revealedHashes, setRevealedHashes] = useState<Record<string, boolean>>({});
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [editingLimitId, setEditingLimitId] = useState<string | null>(null);
  const [editingLimitValue, setEditingLimitValue] = useState<number>(5);

  // 折叠/展开控制状态（默认是折叠的，支持用户操作记忆）
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("tg_api_pool_collapsed");
      if (saved === null) return true; // 默认折叠
      return saved === "true";
    } catch {
      return true;
    }
  });

  const knownOwners = React.useMemo(() => {
    const set = new Set<string>();
    set.add(effectiveUsername);
    accounts.forEach((acc) => {
      if (acc.owner) set.add(acc.owner.toLowerCase().trim());
    });
    credentials.forEach((c) => {
      if (c.owner) set.add(c.owner.toLowerCase().trim());
    });
    return Array.from(set);
  }, [accounts, credentials, effectiveUsername]);

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("tg_api_pool_collapsed", String(next));
      } catch {}
      return next;
    });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token") || "";
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchCredentials = async () => {
    setLoading(true);
    setError(null);
    try {
      let queryParam = "";
      if (isAdmin) {
        if (selectedOwnerFilter === "all") {
          queryParam = "?owner=all";
        } else if (selectedOwnerFilter !== "self" && selectedOwnerFilter) {
          queryParam = `?owner=${encodeURIComponent(selectedOwnerFilter)}`;
        }
      }
      const res = await fetch(`/api/tg-credentials${queryParam}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "获取 API 凭证池失败");
      }
      const data = await res.json();
      setCredentials(data.credentials || []);
      if (data.summary) setSummary(data.summary);
      if (data.recommendedCandidate) setRecommendedCandidate(data.recommendedCandidate);
    } catch (err: any) {
      console.error("Failed to load API credentials:", err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, [refreshTrigger, selectedOwnerFilter, currentUser?.username]);

  const handleCopy = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  const toggleRevealHash = (id: string) => {
    setRevealedHashes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleExpandAccounts = (id: string) => {
    setExpandedAccounts((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleAddCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newApiId.trim() || !newApiHash.trim()) {
      setError("请完整填写 API ID 与 API Hash");
      return;
    }
    const parsedId = Number(newApiId.trim());
    if (isNaN(parsedId) || parsedId <= 0) {
      setError("API ID 必须为有效的正整数");
      return;
    }
    const cleanHash = newApiHash.trim();
    if (!/^[a-f0-9]{32}$/i.test(cleanHash)) {
      setError("API Hash 格式不正确（需为32位十六进制字符串）");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const assignedOwner = effectiveUsername;
      const res = await fetch("/api/tg-credentials", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          apiId: parsedId,
          apiHash: cleanHash,
          title: newTitle.trim() || `API凭证 ${parsedId}`,
          maxAccounts: Number(newMaxAccounts) || 5,
          note: newNote.trim(),
          targetOwner: assignedOwner,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "添加凭证失败");
      }

      setSuccessMsg(`已成功添加 API ID [${parsedId}] 至凭证池（上限: ${newMaxAccounts} 个账号）`);
      setNewApiId("");
      setNewApiHash("");
      setNewTitle("");
      setNewMaxAccounts(5);
      setNewNote("");
      setShowAddForm(false);
      await fetchCredentials();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLimit = async (cred: TgApiCredential, newLimit: number) => {
    if (newLimit < 1) return;
    try {
      const res = await fetch("/api/tg-credentials/update", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          id: cred.id,
          maxAccounts: newLimit,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "更新挂号上限失败");
      }
      setSuccessMsg(`已更新凭证 [${cred.apiId}] 的挂号上限为 ${newLimit} 个账号`);
      setEditingLimitId(null);
      await fetchCredentials();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message || String(err));
    }
  };

  const handleToggleStatus = async (cred: TgApiCredential) => {
    const newStatus = cred.status === "disabled" ? "active" : "disabled";
    try {
      const res = await fetch("/api/tg-credentials/update", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          id: cred.id,
          status: newStatus,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "切换状态失败");
      }
      setSuccessMsg(`凭证 [${cred.apiId}] 状态已设置为: ${newStatus === "active" ? "启用" : "停用"}`);
      await fetchCredentials();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.message || String(err));
    }
  };

  const handleDelete = async (cred: TgApiCredential) => {
    const confirmDelete = window.confirm(
      `确定从凭证池中移除 API ID [${cred.apiId}] 吗？\n\n【重要安全保障】：\n移除后，已使用该凭证登录的账号不会受到任何影响（Session 已永久锁定在原 API ID 上）。后续新账号登录将不再向此凭证分配负载。`
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch("/api/tg-credentials/delete", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: cred.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "删除凭证失败");
      }
      setSuccessMsg(`已将 API ID [${cred.apiId}] 从凭证池中移除（已登录账号不受影响）`);
      await fetchCredentials();
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err: any) {
      setError(err.message || String(err));
    }
  };

  return (
    <div
      id="api-credentials-pool-section"
      className="rounded-2xl border border-slate-800 bg-slate-900 shadow-xl transition-all duration-300 overflow-hidden"
    >
      {/* 顶部标题栏与操作按钮区 */}
      <div
        className={`p-4 sm:p-5 transition-all duration-200 ${
          isCollapsed ? "" : "border-b border-slate-800"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-2xl" role="img" aria-label="key">
              🔑
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-display font-bold tracking-tight text-white">
                API 凭证池
              </h3>
              {isCollapsed && (
                <span className="text-xs px-2.5 py-0.5 rounded-md font-mono font-medium bg-slate-800 text-slate-300 border border-slate-700">
                  已折叠 · {summary.totalCredentials}套 ({summary.activeCredentialsCount}可用) · 挂载 {summary.totalMountedAccounts} 个号
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {/* 管理员视角切换器 (无专属隔离字样) */}
            {isAdmin && (
              <div className="flex items-center gap-1.5 bg-slate-950/80 border border-slate-700 px-2 py-1 rounded-xl">
                <Filter className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[11px] text-slate-400">视角:</span>
                <select
                  value={selectedOwnerFilter}
                  onChange={(e) => setSelectedOwnerFilter(e.target.value)}
                  className="bg-transparent text-xs text-amber-300 font-semibold focus:outline-none cursor-pointer"
                >
                  <option value="self" className="bg-slate-900 text-white">
                    当前用户 ({effectiveUsername})
                  </option>
                  <option value="all" className="bg-slate-900 text-white">
                    全部账号概览
                  </option>
                  {knownOwners
                    .filter((o) => o !== effectiveUsername)
                    .map((owner) => (
                      <option key={owner} value={owner} className="bg-slate-900 text-white">
                        用户: {owner}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <button
              type="button"
              onClick={fetchCredentials}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium transition-all cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-amber-400" : ""}`} />
              刷新池
            </button>
            <button
              type="button"
              onClick={() => {
                if (isCollapsed) setIsCollapsed(false);
                setShowAddForm(!showAddForm);
              }}
              className="flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-xl font-bold bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-md shadow-amber-500/20 transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              {showAddForm && !isCollapsed ? "收起录入表单" : "+ 录入新 API 凭证"}
            </button>
            <button
              type="button"
              onClick={toggleCollapsed}
              className="flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-xl font-medium border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 transition-all cursor-pointer"
              title={isCollapsed ? "展开 API 凭证池窗口" : "折叠 API 凭证池窗口"}
            >
              {isCollapsed ? (
                <>
                  <ChevronDown className="w-4 h-4 text-amber-400" />
                  <span>展开窗口</span>
                </>
              ) : (
                <>
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                  <span>折叠窗口</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 展开状态下的指标概览 Bar */}
        {!isCollapsed && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-800 text-xs">
            <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60">
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mb-1 font-medium">
                <Layers className="w-3.5 h-3.5 text-amber-400" />
                <span>凭证套数</span>
              </div>
              <div className="text-lg font-bold font-mono text-white">
                {summary.totalCredentials}{" "}
                <span className="text-xs font-normal text-slate-400 font-sans">
                  套 ({summary.activeCredentialsCount} 可用)
                </span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60">
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mb-1 font-medium">
                <Users className="w-3.5 h-3.5 text-sky-400" />
                <span>当前池承载账号</span>
              </div>
              <div className="text-lg font-bold font-mono text-sky-400">
                {summary.totalMountedAccounts}{" "}
                <span className="text-xs font-normal text-slate-400 font-sans">个账号</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60">
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mb-1 font-medium">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                <span>下一号推荐分配</span>
              </div>
              <div className="text-xs font-medium truncate text-emerald-400 font-mono">
                {recommendedCandidate?.apiId ? (
                  <>
                    API ID: <span className="font-bold">{recommendedCandidate.apiId}</span>
                    <span className="text-[10px] text-slate-400 ml-1">
                      ({recommendedCandidate.currentAccounts}/{recommendedCandidate.maxAccounts})
                    </span>
                  </>
                ) : (
                  "回退 .env 全局凭证"
                )}
              </div>
            </div>

            <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/60">
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mb-1 font-medium">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
                <span>.env 全局回退保底</span>
              </div>
              <div className="text-xs font-mono text-white font-bold truncate">
                ID: {summary.fallbackApiId || 94575}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 展开状态下显示内容主体 */}
      {!isCollapsed && (
        <>
          {/* 消息提示 */}
          {error && (
            <div className="m-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                <span>{error}</span>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
          )}

          {successMsg && (
            <div className="m-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>{successMsg}</span>
              </div>
              <button
                type="button"
                onClick={() => setSuccessMsg(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
          )}

          {/* 手动录入新 API 凭证折叠表单 (绿色框部分按要求改为单单一列) */}
          {showAddForm && (
            <div className="p-5 m-4 rounded-2xl border border-slate-800 bg-slate-950/80 shadow-lg transition-all">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-400" />
                  录入新 Telegram API 凭证
                </h4>
                <span className="text-[11px] text-slate-400">
                  亦可在左侧登录账号时以「API模式」登录，系统将自动收录入凭证池
                </span>
              </div>

              <form onSubmit={handleAddCredential} className="space-y-4">
                {/* 绿色框字段改为一行排布 */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 w-full">
                  <div className="md:col-span-3">
                    <label className="block text-xs text-slate-300 font-medium mb-1">
                      API ID <span className="text-rose-400 font-bold">*必填</span>
                    </label>
                    <input
                      type="number"
                      placeholder="例如: 29482103"
                      value={newApiId}
                      onChange={(e) => setNewApiId(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-xs font-mono"
                      required
                    />
                  </div>

                  <div className="md:col-span-4">
                    <label className="block text-xs text-slate-300 font-medium mb-1">
                      API Hash <span className="text-rose-400 font-bold">*必填 (32位十六进制)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="例如: e473a623d22291e3976c2cdb0ea729f9"
                      value={newApiHash}
                      onChange={(e) => setNewApiHash(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-xs font-mono"
                      required
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-xs text-slate-300 font-medium mb-1">
                      凭证备注 / 标签 <span className="text-slate-500">(可选)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="例如: 客户端 App 01"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-xs"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-300 font-medium mb-1 flex items-center justify-between">
                      <span>挂号上限数量</span>
                      <span className="text-[10px] text-amber-400 font-bold">默认 5 个账号</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newMaxAccounts}
                      onChange={(e) => setNewMaxAccounts(Math.max(1, Number(e.target.value) || 1))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-amber-500 text-xs font-mono font-bold"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 w-full">
                  <a
                    href="https://my.telegram.org"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-sky-400 hover:text-sky-300 underline flex items-center gap-1 font-medium"
                  >
                    前往 my.telegram.org 免费申请 API (API development tools)
                    <ExternalLink className="w-3 h-3" />
                  </a>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-3.5 py-1.5 rounded-xl border border-slate-700 bg-slate-800 text-xs text-slate-300 hover:bg-slate-700 font-medium cursor-pointer"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-md shadow-amber-500/20 disabled:opacity-50 cursor-pointer"
                    >
                      {submitting ? "正在保存..." : "确认添加到凭证池"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* 凭证列表展示 */}
          <div className="p-5 sm:p-6 space-y-4">
            {credentials.length === 0 ? (
              <div className="text-center py-10 px-4 border border-dashed border-slate-800 rounded-2xl bg-slate-950/40">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center mx-auto mb-3">
                  <Key className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-white">当前 API 凭证池为空</h4>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {credentials.map((cred) => {
                  const activeCount = cred.activeAccountCount || 0;
                  const maxLimit = cred.maxAccounts || 5;
                  const isFull = activeCount >= maxLimit;
                  const isRecommended =
                    recommendedCandidate &&
                    Number(recommendedCandidate.apiId) === Number(cred.apiId) &&
                    cred.status !== "disabled";
                  const isRevealed = revealedHashes[cred.id] || false;
                  const isAccountsExpanded = expandedAccounts[cred.id] || false;
                  const isEditingLimit = editingLimitId === cred.id;

                  // 计算负载百分比
                  const pct = Math.min(100, Math.round((activeCount / maxLimit) * 100));

                  return (
                    <div
                      key={cred.id}
                      className={`rounded-2xl border transition-all duration-200 overflow-hidden flex flex-col justify-between ${
                        isRecommended
                          ? "border-amber-500/60 bg-slate-900 ring-1 ring-amber-500/30 shadow-lg shadow-amber-500/5"
                          : cred.status === "disabled"
                          ? "border-slate-800/60 bg-slate-950/40 opacity-60"
                          : "border-slate-800 bg-slate-900 hover:border-slate-700 shadow-md"
                      }`}
                    >
                      {/* 卡片头部 */}
                      <div className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-0.5 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-sm truncate text-white">
                                {cred.title || `API 凭证 ${cred.apiId}`}
                              </span>
                              {isRecommended && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-slate-950 flex items-center gap-1 shadow-xs">
                                  <Sparkles className="w-3 h-3" />
                                  推荐分配 (最少挂号)
                                </span>
                              )}
                              {isFull && cred.status !== "disabled" && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20">
                                  已达上限
                                </span>
                              )}
                              {cred.status === "disabled" && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-400">
                                  已停用
                                </span>
                              )}
                            </div>

                            {cred.boundPhone && (
                              <div className="pt-0.5">
                                <span className="text-[10px] px-2 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/30 text-sky-300 font-mono flex items-center gap-1 inline-flex">
                                  <Smartphone className="w-2.5 h-2.5 text-sky-400" />
                                  {cred.boundPhone}
                                </span>
                              </div>
                            )}

                            {cred.note && (
                              <div className="text-[11px] text-slate-400 truncate pt-0.5" title={cred.note}>
                                {cred.note}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(cred)}
                              className={`text-[11px] px-2 py-1 rounded-lg border transition cursor-pointer font-medium ${
                                cred.status === "disabled"
                                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20"
                                  : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
                              }`}
                              title={cred.status === "disabled" ? "点击启用此凭证" : "点击停用此凭证"}
                            >
                              {cred.status === "disabled" ? "启用" : "停用"}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(cred)}
                              className="p-1.5 text-slate-500 hover:text-rose-400 rounded-lg hover:bg-rose-500/10 transition cursor-pointer"
                              title="从凭证池中删除 (已登录账号不受影响)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* API ID 与 API HASH 详情 */}
                        <div className="space-y-2 pt-2 border-t border-slate-800/80 font-mono text-xs">
                          <div className="flex items-center justify-between bg-slate-950/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                            <span className="text-[10px] text-slate-400 font-sans font-medium">API ID:</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-white">{cred.apiId}</span>
                              <button
                                type="button"
                                onClick={() => handleCopy(String(cred.apiId), `id_${cred.id}`)}
                                className="text-slate-400 hover:text-slate-200 p-0.5 cursor-pointer"
                                title="复制 API ID"
                              >
                                {copiedKey === `id_${cred.id}` ? (
                                  <Check className="w-3 h-3 text-emerald-400" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center justify-between bg-slate-950/60 px-2.5 py-1.5 rounded-xl border border-slate-800">
                            <span className="text-[10px] text-slate-400 font-sans font-medium">API Hash:</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-300 truncate max-w-[140px] font-mono">
                                {isRevealed
                                  ? cred.apiHash
                                  : cred.apiHash.slice(0, 6) + "••••••••" + cred.apiHash.slice(-4)}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleRevealHash(cred.id)}
                                className="text-slate-400 hover:text-slate-200 p-0.5 cursor-pointer"
                                title={isRevealed ? "隐藏 Hash" : "显示明文 Hash"}
                              >
                                {isRevealed ? (
                                  <EyeOff className="w-3 h-3" />
                                ) : (
                                  <Eye className="w-3 h-3" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCopy(cred.apiHash, `hash_${cred.id}`)}
                                className="text-slate-400 hover:text-slate-200 p-0.5 cursor-pointer"
                                title="复制 API Hash"
                              >
                                {copiedKey === `hash_${cred.id}` ? (
                                  <Check className="w-3 h-3 text-emerald-400" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* 挂号上限数量与负载进度条 */}
                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-400 flex items-center gap-1 font-medium">
                              <Users className="w-3.5 h-3.5 text-slate-500" />
                              <span>挂号负载:</span>
                              <span
                                className={`font-mono font-bold ${
                                  isFull
                                    ? "text-rose-400"
                                    : pct > 60
                                    ? "text-amber-400"
                                    : "text-emerald-400"
                                }`}
                              >
                                {activeCount}
                              </span>
                              <span className="text-slate-500">/</span>
                              <span className="font-mono text-slate-300 font-bold">{maxLimit}</span>
                              <span className="text-[10px] text-slate-500">个账号</span>
                            </span>

                            {!isEditingLimit ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingLimitId(cred.id);
                                  setEditingLimitValue(maxLimit);
                                }}
                                className="text-[11px] text-amber-400 hover:text-amber-300 font-semibold flex items-center gap-0.5 cursor-pointer underline"
                              >
                                <Sliders className="w-3 h-3" />
                                设置上限
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={editingLimitValue}
                                  onChange={(e) =>
                                    setEditingLimitValue(
                                      Math.max(1, Number(e.target.value) || 1)
                                    )
                                  }
                                  className="w-14 px-1.5 py-0.5 text-xs bg-slate-900 border border-amber-500 rounded text-center text-white font-mono font-bold shadow-xs"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => handleUpdateLimit(cred, editingLimitValue)}
                                  className="px-2 py-0.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-[10px] font-bold rounded cursor-pointer"
                                >
                                  保存
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingLimitId(null)}
                                  className="px-1.5 py-0.5 text-slate-400 hover:text-white text-[10px] cursor-pointer"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>

                          {/* 进度条 */}
                          <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                isFull
                                  ? "bg-rose-500"
                                  : pct > 80
                                  ? "bg-amber-500"
                                  : pct > 40
                                  ? "bg-sky-500"
                                  : "bg-emerald-500"
                              }`}
                              style={{ width: `${Math.min(100, Math.max(5, pct))}%` }}
                            />
                          </div>
                        </div>

                        {/* 查看已挂载账号列表展开 */}
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => toggleExpandAccounts(cred.id)}
                            className="w-full text-[11px] text-slate-400 hover:text-slate-200 py-1 flex items-center justify-between border-t border-slate-800/60 cursor-pointer font-medium"
                          >
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3 text-slate-500" />
                              已挂载账号 ({activeCount})
                            </span>
                            {isAccountsExpanded ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </button>

                          {isAccountsExpanded && (
                            <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto pr-1">
                              {cred.accountList && cred.accountList.length > 0 ? (
                                cred.accountList.map((acc, idx) => (
                                  <div
                                    key={idx}
                                    className="px-2 py-1 rounded bg-slate-950/80 border border-slate-800 text-[11px] flex items-center justify-between"
                                  >
                                    <span className="font-mono text-slate-200 font-medium">{acc.phoneNumber}</span>
                                    <div className="flex items-center gap-1.5">
                                      {acc.username && (
                                        <span className="text-slate-400 truncate max-w-[80px]">
                                          @{acc.username}
                                        </span>
                                      )}
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${
                                          acc.status === "connected"
                                            ? "bg-emerald-400"
                                            : "bg-slate-500"
                                        }`}
                                      />
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-[11px] text-slate-500 text-center py-2">
                                  暂无挂载账号，等待分配中
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 卡片底栏：快捷登录使用该凭证 */}
                      {onSelectApiForLogin && (
                        <div className="px-4 py-2.5 bg-slate-950/40 border-t border-slate-800 flex items-center justify-between">
                          <span className="text-[10px] text-slate-500 font-mono">
                            ID: {cred.apiId}
                          </span>
                          <button
                            type="button"
                            onClick={() => onSelectApiForLogin(cred.apiId, cred.apiHash)}
                            className="text-xs text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1 cursor-pointer"
                          >
                            填入左侧登录表单
                            <ArrowDownRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
